import { useEffect, useMemo, useRef, useState } from "react";
import { COACH_SEES_ENGINE } from "../lib/coachEngine";
import { myId } from "../lib/me";
import { supabase } from "../lib/supabase";
import { href, routeQuery } from "../lib/route";
import { Workspace } from "../components/Shell";
import { LibraryTabs, useLibraryCounts } from "../components/LibraryTabs";
import { ExerciseShelf, ShelfViewSwitch, useShelfView } from "../components/ExerciseShelf";
import { CATEGORY_LABEL, CATEGORY_ORDER, CATEGORIES, MOVEMENT_PATTERNS, type MovementPattern } from "../lib/coachExercise";
import { muscleLabel, vocabularyLabel } from "../lib/vocabulary";
import { useQuerySort } from "../lib/useQuerySort";
import {
  PAGE_SIZE, clampPage, deleteRefusal, lastPage, lastUsedByExercise, libraryQuery, pageLabel, pageRows,
  sortRows, splitDeletable, usedInWorkouts, viewFromQuery,
  type LibraryView, type SortKey,
} from "../lib/exerciseTable";
import {
  EMPTY_SHELF_FILTER, applyHide, filterShelf, hideWrite, shelfEmptyReason, shelfCountLine, shelfFilterIsOn, equipmentPieces, shelfFoot, usedInByExercise,
  type ShelfFilter, type ShelfRow,
} from "../lib/exerciseShelf";
import { copyDepsFrom, copyExercise } from "../lib/copyExercise";
import { hasRule } from "../lib/ruleSentence";

/* THE LIBRARY — EXERCISES (W72 step 5, Proposal 5 with his 2026-09-25 ~02:20 yes: *"yes just without these strange
 * chips above or below each exercise row"*).
 *
 * The page: `Library` as the title, the four parts as ONE underline row under it (`LibraryTabs` — the left sub-nav and
 * its phone copy are gone), ONE primary `New exercise` top-right. Then ONE search that finds an exercise by what it IS
 * (name + its generated long forms, muscle, equipment — `shelfMatches`, the same function the builder's pane asks),
 * the filter chips beside it (muscle · kind · equipment · No rule · Hidden), the List · Grid switch at the row's right,
 * and the rows: 56 px, the picture, the name with its caption TEXT (also called · equipment · kind), Trains, Level,
 * Used in, and on hover Edit (the pencil) and ONE `⋯` holding Select · Copy · Hide (job 3 step 5 page 4). A foot line says what
 * is shown and how many exercises in a workout carry no rule. No checkbox column: bulk actions appear after a selection.
 *
 * 🔴 WHAT OF THE COPIED TABLE SURVIVES, AND WHY. The SORT IN THE URL (W8/§6.7's property — `#/exercises?sort=…` is an
 * address a coach can paste) and the PAGE (50 a page, `1 – 50 of 144`) survive, through the same pure functions
 * (`viewFromQuery`, `libraryQuery`, `sortRows`, `pageRows`) and their tests. What went: the checkbox column, the seven
 * sortable heads (Pattern and Records became the search and the No-rule chip — Proposal 5's "Weighed"), the Filter
 * popover (its facts are the chips now), `Has a video` (not in Proposal 5's chip list; a pasted `video_only=true` no
 * longer narrows anything, which is safer than a narrowing with no chip to show it), and the narrow-width Select mode
 * (the `⋯ → Select` path works at every width).
 */

/** The sorts this page offers, and their words. The URL still parses every key the copied table wrote. */
const ALL_SORTS: readonly { key: SortKey; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "lastUsed", label: "Last used" },
  { key: "difficulty", label: "Level" },
  { key: "muscles", label: "Trains" },
];
/** `Level` is engine data, withheld from coaches for now (`COACH_SEES_ENGINE`, his 2026-09-28 word). */
const SORT_OPTIONS = ALL_SORTS.filter((o) => COACH_SEES_ENGINE || o.key !== "difficulty");

export function Exercises({ reloadKey = 0, foreground = true }: {
  reloadKey?: number;
  /** False while the editor's window is drawn over this screen — see the focus effect. */
  foreground?: boolean;
}) {
  const [rows, setRows] = useState<ShelfRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ShelfFilter>(EMPTY_SHELF_FILTER);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [inUse, setInUse] = useState<ReadonlySet<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const [localReload, setLocalReload] = useState(0);
  const [urlView, setUrlViewState] = useQuerySort<LibraryView>(viewFromQuery, routeQuery);
  const [view, setView] = useShelfView("exercises");
  const counts = useLibraryCounts(reloadKey + localReload);
  const heading = useRef<HTMLHeadingElement>(null);

  /* Focus moves to the heading whenever this screen is the one in front — it is the BACKDROP behind the editor's
     window and does not remount, so a mount-only effect could never hand focus back. */
  useEffect(() => { if (foreground) heading.current?.focus(); }, [foreground]);

  /* ONE WRITER FOR THE URL — `libraryQuery` is the only place these keys are spelled; `replaceState` because a sort is
     not a place the back button should walk through. A new sort or filter goes back to page 1. */
  function setUrlView(next: LibraryView) {
    setUrlViewState(next);
    window.history.replaceState(null, "", `${href({ name: "exercises" })}${libraryQuery(next)}`);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // OWNER-NAMED (`lib/me.ts`): `coach_exercises_client_read` also admits a coach's active clients, so for a coach
      // who is somebody's client RLS returns two libraries.
      const { data, error: err } = await supabase
        .from("coach_exercises")
        .select("id, name, movement_pattern, category, equipment, primary_muscles, tracking_fields, difficulty, demo_video_url, thumbnail_url, progression_config, hidden_at")
        .eq("coach_id", await myId())
        .order("name");
      if (err) { if (!cancelled) setError(err.message); return; }
      if (cancelled) return;
      /* THREE FLAT READS, NO NESTED EMBED (an embed arrives unordered and as an object OR an array). A failure is NOT
         fatal: Used in and Last used read as unknown and the rest still works. */
      const [wk, secs, wex, asg] = await Promise.all([
        supabase.from("coach_workouts").select("id, client_id, archived_at"),
        supabase.from("coach_workout_sections").select("id, workout_id"),
        supabase.from("coach_workout_exercises").select("exercise_id, section_id"),
        supabase.from("coach_assignments").select("workout_id, scheduled_date"),
      ]);
      if (cancelled) return;
      const lastUsed = (asg.error ?? secs.error ?? wex.error) !== null
        ? new Map<string, string>() : lastUsedByExercise(asg.data ?? [], secs.data ?? [], wex.data ?? []);
      const usedIn = (wk.error ?? secs.error ?? wex.error) !== null
        ? new Map<string, number>() : usedInByExercise(wk.data ?? [], secs.data ?? [], wex.data ?? []);
      // A row in ANY workout (a client's copy included) cannot be deleted — the FK is RESTRICT.
      setInUse(wex.error !== null ? new Set<string>() : usedInWorkouts(wex.data ?? []));
      setRows((data ?? []).map((r) => ({
        id: r.id,
        name: r.name,
        movementPattern: (MOVEMENT_PATTERNS as readonly string[]).includes(r.movement_pattern ?? "")
          ? (r.movement_pattern as MovementPattern) : null,
        muscles: r.primary_muscles ?? [],
        equipment: r.equipment ?? [],
        trackingFields: r.tracking_fields ?? [],
        difficulty: r.difficulty,
        category: (CATEGORIES as readonly string[]).includes(r.category ?? "") ? r.category : null,
        lastUsedISO: lastUsed.get(r.id) ?? null,
        demoVideoUrl: r.demo_video_url,
        thumbnailUrl: r.thumbnail_url,
        usedIn: usedIn.get(r.id) ?? 0,
        hiddenAt: r.hidden_at,
        progressionConfig: r.progression_config,
      })));
    })();
    return () => { cancelled = true; };
    // `reloadKey` is bumped by `App.tsx` when the editor saves; `localReload` by a Copy from a row.
  }, [reloadKey, localReload]);

  /* THE CHIPS OFFER ONLY WHAT THE COACH HAS — a filter that can return nothing wastes a tap and teaches distrust.
     Muscles are one chip per LABEL (the library holds `biceps` AND `Biceps`). */
  const onShelf = useMemo(() => (rows ?? []).filter((r) => r.hiddenAt === null), [rows]);
  const muscles = useMemo(() => [...new Set(onShelf.flatMap((r) => r.muscles.map((m) => muscleLabel(m).label)))]
    .sort((a, b) => a.localeCompare(b)), [onShelf]);
  const kinds = useMemo(() => CATEGORY_ORDER.filter((c) => onShelf.some((r) => r.category === c)), [onShelf]);
  const equipment = useMemo(() => [...new Set(onShelf.flatMap((r) => equipmentPieces(r.equipment)))]
    .sort((a, b) => vocabularyLabel(a).localeCompare(vocabularyLabel(b))), [onShelf]);
  const hiddenCount = (rows ?? []).length - onShelf.length;

  /* FILTER, THEN SORT, THEN PAGE — three pure functions, none of them here. */
  const shown = useMemo(
    () => sortRows(filterShelf(rows ?? [], filter, hasRule), urlView.key, urlView.dir) as ShelfRow[],
    [rows, filter, urlView.key, urlView.dir],
  );
  const page = clampPage(urlView.page, shown.length);
  const paged = useMemo(() => pageRows(shown, page), [shown, page]);
  /* ⚠️ EVERYTHING A BULK ACTION DOES READS THE SELECTION PRUNED TO WHAT IS SHOWN — tick three, type a search that
     hides them, press Delete, and three rows a coach cannot see would go. */
  const selectedShown = useMemo(() => shown.filter((r) => selected.has(r.id)), [shown, selected]);

  function setFilterAndFirstPage(next: ShelfFilter) {
    setFilter(next);
    if (urlView.page !== 1) setUrlView({ ...urlView, page: 1 });
  }

  async function copy(id: string) {
    const { data: s } = await supabase.auth.getSession();
    const coachId = s.session?.user.id;
    if (coachId === undefined) { setError("You are signed out. Sign in again."); return; }
    setWorking(id);
    const made = await copyExercise(copyDepsFrom(supabase), id, coachId);
    setWorking(null);
    if (!made.ok) { setError(made.message); return; }
    setLocalReload((n) => n + 1);
  }

  async function hide(ids: readonly string[], hideThem: boolean) {
    if (ids.length === 0) return;
    const write = hideWrite(hideThem, new Date());
    setWorking(ids.length === 1 ? ids[0]! : "bulk");
    const { error: err } = await supabase.from("coach_exercises").update(write).in("id", [...ids]);
    setWorking(null);
    if (err !== null) { setError(err.message); return; }
    setRows((current) => ids.reduce((acc, id) => applyHide(acc, id, write), current ?? []));
    setSelected(new Set());
    setLocalReload((n) => n + 1);
  }

  async function deleteSelected() {
    const { deletable } = splitDeletable(selectedShown, inUse);
    setConfirmDelete(false);
    if (deletable.length === 0) return;
    setWorking("bulk");
    const { error: err } = await supabase.from("coach_exercises").delete().in("id", deletable.map((r) => r.id));
    setWorking(null);
    const refusal = deleteRefusal(err);
    if (refusal !== null) { setError(refusal); return; }
    setRows((current) => (current ?? []).filter((r) => !deletable.some((d) => d.id === r.id)));
    setSelected(new Set());
    setLocalReload((n) => n + 1);
  }

  return (
    <Workspace>
      <main id="main" className="exercises-page ours-page library-page">
        <header className="ours-head">
          <div className="ours-head-text">
            <h1 className="ht-title ours-title" tabIndex={-1} ref={heading}>Library</h1>
          </div>
          {/* This screen's ONE `.ht-button-primary`. */}
          <a className="ht-button ht-button-primary" href={href({ name: "exerciseNew" })}>New exercise</a>
        </header>
        <LibraryTabs current="exercises" counts={counts} />

        {error !== null && <p className="ht-callout ours-error" role="alert">{error}</p>}
        {rows === null && error === null && <p className="ht-callout ours-quiet" aria-live="polite">Loading…</p>}

        {rows !== null && rows.length === 0 && (
          <p className="ht-callout ours-quiet">
            No exercises yet. An exercise is one movement — you name it, cue it and film it once, then build any
            workout from it.
          </p>
        )}

        {rows !== null && rows.length > 0 && (
          <div className="shelf-controls">
            <label className="ours-search">
              <span className="ht-visually-hidden">Search exercises</span>
              <input className="ht-input" type="search" value={filter.query}
                     placeholder="Name, muscle or equipment"
                     onChange={(e) => setFilterAndFirstPage({ ...filter, query: e.target.value })} />
            </label>
            {/* THE CHIPS — here and nowhere inside a row. Three are menus of what the coach HAS; two are toggles. */}
            <div className="ours-chips" role="group" aria-label="Filter">
              <ChipSelect label="Muscle" value={filter.muscle} options={muscles.map((m) => ({ value: m, label: m }))}
                          onPick={(v) => setFilterAndFirstPage({ ...filter, muscle: v })} />
              <ChipSelect label="Kind" value={filter.kind}
                          options={kinds.map((k) => ({ value: k, label: CATEGORY_LABEL[k] }))}
                          onPick={(v) => setFilterAndFirstPage({ ...filter, kind: v })} />
              <ChipSelect label="Equipment" value={filter.equipment}
                          options={equipment.map((k) => ({ value: k, label: vocabularyLabel(k) }))}
                          onPick={(v) => setFilterAndFirstPage({ ...filter, equipment: v })} />
              {COACH_SEES_ENGINE && (
                <button type="button" className="ours-chip" aria-pressed={filter.noRule}
                        onClick={() => setFilterAndFirstPage({ ...filter, noRule: !filter.noRule })}>No rule</button>
              )}
              {hiddenCount > 0 && (
                <button type="button" className="ours-chip" aria-pressed={filter.hidden}
                        onClick={() => setFilterAndFirstPage({ ...filter, hidden: !filter.hidden })}>
                  Hidden <span className="ours-chip-count">{hiddenCount}</span>
                </button>
              )}
              {shelfFilterIsOn(filter) && (
                <button type="button" className="ours-text-action"
                        onClick={() => setFilterAndFirstPage({ ...EMPTY_SHELF_FILTER, query: filter.query })}>Clear</button>
              )}
            </div>
            <label className="ours-sort">
              Sort
              <select value={urlView.key}
                      onChange={(e) => setUrlView({ ...urlView, key: e.target.value as SortKey,
                                                    dir: e.target.value === "lastUsed" ? "desc" : "asc", page: 1 })}>
                {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </label>
            <ShelfViewSwitch view={view} onChange={setView} name="exercises-view" />
          </div>
        )}

        {selectedShown.length > 0 && (() => {
          const { deletable, blocked } = splitDeletable(selectedShown, inUse);
          return (
            <div className="shelf-bulk" role="group" aria-label="Selected exercises">
              <strong>{selectedShown.length} selected</strong>
              <button type="button" className="ours-text-action" disabled={working !== null}
                      onClick={() => { void hide(selectedShown.map((r) => r.id), !filter.hidden); }}>
                {filter.hidden ? `Unhide ${String(selectedShown.length)}` : `Hide ${String(selectedShown.length)}`}
              </button>
              {deletable.length > 0 && (
                <button type="button" className="ours-text-action" disabled={working !== null}
                        onClick={() => setConfirmDelete(true)}>
                  {deletable.length === selectedShown.length ? `Delete ${String(deletable.length)}` : `Delete the other ${String(deletable.length)}`}
                </button>
              )}
              {blocked.length > 0 && (
                <span className="ours-caption">
                  {blocked.length === 1 ? "1 is in a workout and cannot be deleted" : `${String(blocked.length)} are in workouts and cannot be deleted`}
                </span>
              )}
              <button type="button" className="ours-text-action" onClick={() => setSelected(new Set())}>Clear selection</button>
            </div>
          );
        })()}

        {confirmDelete && (() => {
          const { deletable } = splitDeletable(selectedShown, inUse);
          return (
            <div className="shelf-bulk" role="alertdialog" aria-label="Confirm delete">
              <span>Delete {deletable.length === 1 ? "this exercise" : `these ${String(deletable.length)} exercises`}? This cannot be undone. {deletable.map((r) => r.name).join(", ")}</span>
              <button type="button" className="ours-text-action" onClick={() => setConfirmDelete(false)}>Keep them</button>
              <button type="button" className="ours-text-action" disabled={working !== null}
                      onClick={() => { void deleteSelected(); }}>Delete</button>
            </div>
          );
        })()}

        {rows !== null && rows.length > 0 && shown.length === 0 && (
          <p className="ht-callout ours-quiet">
            {shelfEmptyReason(filter)}
          </p>
        )}

        {paged.length > 0 && (
          <ExerciseShelf variant="page" view={view} rows={paged} selected={selected} onSelect={setSelected}
                         onCopy={(id) => { void copy(id); }} onHide={(id, h) => { void hide([id], h); }} busy={working} />
        )}

        {shown.length > PAGE_SIZE && (
          <nav className="shelf-pager" aria-label="Pages">
            <button type="button" className="ours-text-action" disabled={page <= 1}
                    onClick={() => setUrlView({ ...urlView, page: page - 1 })}>‹ Previous</button>
            <span aria-live="polite">{pageLabel(shown.length, page)}</span>
            <button type="button" className="ours-text-action" disabled={page >= lastPage(shown.length)}
                    onClick={() => setUrlView({ ...urlView, page: page + 1 })}>Next ›</button>
          </nav>
        )}

        {/* The foot says only what the tab does not: `N of M` under a filter (or the engine's rule count). Unfiltered the
            count alone was the tab's own number again (`exerciseCount` counts the same shelf) — cut in job 3 step 5 page 4. */}
        {rows !== null && rows.length > 0 && (COACH_SEES_ENGINE || shown.length !== onShelf.length)
          && <p className="ours-foot">{COACH_SEES_ENGINE ? shelfFoot(shown, rows, hasRule) : shelfCountLine(shown, rows)}</p>}
      </main>
    </Workspace>
  );
}

/** A filter chip that opens a menu of values — a native `<select>` drawn as a chip, so the keyboard, the screen
 *  reader and the phone's own picker come for free. The chip reads `Muscle` until a value is chosen, then the value. */
function ChipSelect({ label, value, options, onPick }: {
  label: string; value: string | null; options: readonly { value: string; label: string }[];
  onPick: (v: string | null) => void;
}) {
  if (options.length < 2 && value === null) return null;
  return (
    <label className="shelf-chip-select">
      <span className="ht-visually-hidden">{label}</span>
      <select className="ours-chip" data-on={value !== null} value={value ?? ""}
              onChange={(e) => onPick(e.target.value === "" ? null : e.target.value)}>
        <option value="">{label}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
